import { useMemo, useState } from "react";
import type { ReactNode } from "react";
import {
  Laptop,
  Loader2,
  Mail,
  MonitorSmartphone,
  Phone,
  ShieldAlert,
  ShieldCheck,
  Smartphone,
} from "lucide-react";
import {
  useAppSessions,
  useDevices,
  useProfile,
  useRestoreDevice,
  useRetireDevice,
  useRevokeBinding,
  useRevokeDevice,
  useRevokeSession,
} from "@empite/session";
import type {
  AccountProfile,
  AppSession,
  Binding,
  Device,
  RevokeResult,
  SessionInfo,
} from "@empite/api-client";
import { useCustomerApplications } from "../applications";
import type { PortalApplication } from "../applications";
import { AppBadge } from "../ui/app-badge";
import { Avatar } from "../ui/avatar";
import { Button } from "../ui/button";
import { Card, CardBody, CardFooter, CardHeader } from "../ui/card";
import { CopyButton } from "../ui/copy-button";
import { DataTable } from "../ui/data-table";
import type { Column } from "../ui/data-table";
import { EmptyState } from "../ui/empty-state";
import {
  formatDateTime,
  formatTimestamp,
  humanizeState,
  relativeTime,
  truncateId,
} from "../ui/format";
import { Notice } from "../ui/notice";
import { PageHeader } from "../ui/page-header";
import { UiLink } from "../ui/router";
import { Skeleton } from "../ui/skeleton";
import { StatusPill, outcomeTone } from "../ui/status-pill";
import { appIdentity } from "./billing-common";
import { canSpend, errorMessage, RequireSession } from "./guards";

/**
 * Profile & security — the one screen a customer opens after "was that
 * you?".
 *
 * Everything on it is authenticated by the suite SSO cookie rather than by a
 * device-bound token, which is the whole point: the browser used to sign a
 * lost laptop out of the account is not that laptop.
 *
 * What this screen can do to a device stops at signing this account out of
 * it. Retiring the installation itself — the "it was stolen, kill the key"
 * operation — is deliberately not here: a device is now shared across users
 * over time, so one user retiring it would brick the machine for everybody.
 *
 * The account's raw event log is deliberately NOT here either. A person
 * answering "was that you?" is asking about the machines that can reach the
 * account, and a page of `token.refresh` rows buried the devices card that
 * actually answers it. The trail is still the person's own data and
 * `GET /account/security-events` still serves it — the portal simply stopped
 * being the thing that reads it.
 */
export function Security() {
  return <RequireSession>{(session) => <SecurityPanels session={session} />}</RequireSession>;
}

function SecurityPanels({ session }: { session: SessionInfo }) {
  // One banner for the whole screen. The cards act in different places but
  // land here, so a customer who clicks a button at the bottom of
  // the sessions table still sees that it worked. The TITLE travels with the
  // message rather than being fixed on the banner: signing a device out and
  // revoking a session are no longer the same act, and a banner that called
  // both of them "revoked" would re-tell the lie the buttons stopped telling.
  const [notice, setNotice] = useState<DoneNotice | null>(null);
  // The customer's own applications, only so a session can be drawn with the
  // same badge the rest of the portal gives that application. Nothing on this
  // screen is scoped to one application.
  const { applications } = useCustomerApplications();

  return (
    <>
      <PageHeader
        title="Profile & security"
        description="Your contact details, the installations bound to this account, and the applications signed in on it."
        actions={
          <Button href="/device-approvals" variant="secondary">
            Device approvals
          </Button>
        }
      />

      {notice ? (
        <Notice
          tone="success"
          title={notice.title}
          icon={<ShieldCheck className="size-4" aria-hidden />}
          className="mb-6"
        >
          {notice.body}
        </Notice>
      ) : null}

      <div className="space-y-5">
        <ProfileCard session={session} />
        <DevicesCard onDone={setNotice} />
        <SessionsCard onDone={setNotice} applications={applications} />
      </div>
    </>
  );
}

/** The success banner, as the card that succeeded words it. */
interface DoneNotice {
  title: string;
  body: string;
}

/** "3 sessions ended" rather than "done": an action that reports nothing
 *  leaves the user unsure the button did anything.
 *
 *  `verb` is the caller's, because the same RevokeResult now comes back from
 *  two different promises: signing a device out (its registration survives)
 *  and revoking a session (it does not). */
function endedSummary(verb: string, subject: string, result: RevokeResult): string {
  const parts: string[] = [];
  if (result.sessions_revoked) {
    parts.push(`${result.sessions_revoked} session${result.sessions_revoked === 1 ? "" : "s"}`);
  }
  if (result.bindings_revoked) {
    parts.push(`${result.bindings_revoked} application${result.bindings_revoked === 1 ? "" : "s"}`);
  }
  if (result.refresh_families_revoked) {
    parts.push(
      `${result.refresh_families_revoked} sign-in${result.refresh_families_revoked === 1 ? "" : "s"} that would have renewed`,
    );
  }
  return parts.length === 0 ? `${verb} ${subject}.` : `${verb} ${subject} — ended ${parts.join(", ")}.`;
}

/** The label a revoke button carries while its mutation is in flight. The
 *  ported Button has no `busy` prop, so the spinner is the caller's. */
function BusyLabel({ busy, idle, busyLabel }: { busy: boolean; idle: string; busyLabel: string }) {
  return busy ? (
    <>
      <Loader2 className="size-3.5 animate-spin" aria-hidden />
      {busyLabel}
    </>
  ) : (
    <>{idle}</>
  );
}

function verifiedPill(verified: boolean) {
  return (
    <StatusPill tone={verified ? "success" : "warning"}>
      {verified ? "verified" : "unverified"}
    </StatusPill>
  );
}

/* -- profile -------------------------------------------------------------- */

/** One contact point as the card draws it — an address or a masked number. */
interface Contact {
  key: string;
  value: string;
  verified: boolean;
  primary: boolean;
}

function contactPoints(account: AccountProfile): { emails: Contact[]; phones: Contact[] } {
  return {
    emails: account.emails.map((email) => ({
      key: email.address,
      value: email.address,
      verified: email.verified,
      primary: email.is_primary,
    })),
    // The number itself is stored encrypted and never leaves the database —
    // the last digits are what identifies it to its owner.
    phones: account.phones.map((phone) => ({
      key: `${phone.country_code}-${phone.last_digits}`,
      value: `+${phone.country_code} ••• ${phone.last_digits}`,
      verified: phone.verified,
      primary: phone.is_primary,
    })),
  };
}

function ContactList({
  title,
  icon,
  contacts,
  empty,
}: {
  title: string;
  icon: ReactNode;
  contacts: Contact[];
  empty: string;
}) {
  return (
    <div className="rounded-card border border-line-soft bg-surface-sunken/40 p-4">
      <p className="flex items-center gap-2 text-2xs font-bold tracking-[0.07em] text-ink-4 uppercase">
        <span aria-hidden className="text-ink-4">
          {icon}
        </span>
        {title}
      </p>
      {contacts.length === 0 ? (
        <p className="mt-3 text-sm text-ink-4">{empty}</p>
      ) : (
        <ul className="mt-3 space-y-2.5">
          {contacts.map((contact) => (
            <li key={contact.key} className="flex flex-wrap items-center gap-2">
              <span className="min-w-0 truncate text-sm font-semibold text-ink">
                {contact.value}
              </span>
              {contact.primary ? <StatusPill tone="brand">primary</StatusPill> : null}
              {verifiedPill(contact.verified)}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ProfileCard({ session }: { session: SessionInfo }) {
  const profile = useProfile();

  if (profile.isPending) {
    return (
      <Card>
        <CardBody className="flex items-start gap-5">
          <Skeleton className="size-14 rounded-full" />
          <div className="flex-1 space-y-3">
            <Skeleton className="h-6 w-56" />
            <Skeleton className="h-4 w-72" />
            <Skeleton className="h-24 w-full" />
          </div>
        </CardBody>
      </Card>
    );
  }
  if (profile.error || !profile.data) {
    return (
      <Notice tone="danger" title="Could not load your profile">
        {errorMessage(profile.error)}
      </Notice>
    );
  }

  const account = profile.data;
  const { emails, phones } = contactPoints(account);
  const name = account.display_name?.trim() || emails[0]?.value || "Your account";
  const unverifiedPhone = phones.some((phone) => !phone.verified);

  return (
    <Card>
      <CardBody className="flex flex-wrap items-start gap-5 sm:flex-nowrap sm:gap-6">
        <Avatar initials={(name.trim()[0] ?? "?").toUpperCase()} className="size-14 text-md" />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="font-display text-xl font-bold tracking-[-0.02em] text-ink">{name}</h2>
            <StatusPill tone={outcomeTone(account.status)} dot>
              {humanizeState(account.status)}
            </StatusPill>
            <StatusPill tone={canSpend(session) ? "success" : "warning"}>
              {humanizeState(account.registration_state)}
            </StatusPill>
          </div>
          <p className="mt-1.5 text-sm text-ink-4">
            Member since {formatDateTime(account.created_at)}
            {account.last_login_at
              ? ` · last signed in ${relativeTime(account.last_login_at)}`
              : null}
          </p>

          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <ContactList
              title="Email"
              icon={<Mail className="size-3.5" />}
              contacts={emails}
              empty="No address on file"
            />
            <ContactList
              title="Phone"
              icon={<Phone className="size-3.5" />}
              contacts={phones}
              empty="No number on file"
            />
          </div>

          {unverifiedPhone ? (
            <Notice
              tone="warning"
              title="One of your numbers is not verified"
              icon={<ShieldAlert className="size-4" aria-hidden />}
              className="mt-4"
            >
              You cannot activate an application or spend credit until your phone number is
              verified.{" "}
              <UiLink
                href="/verify-phone"
                search={{ user_id: session.user_id, next: "/security" }}
                className="font-semibold text-brand hover:text-brand-strong"
              >
                Verify it now
              </UiLink>
              .
            </Notice>
          ) : null}
        </div>
      </CardBody>
      <CardFooter className="flex flex-wrap items-center gap-x-6 gap-y-2 text-2xs text-ink-4">
        <span className="flex items-center gap-1.5">
          Account
          <code className="font-mono text-ink-3">{truncateId(account.user_id)}</code>
          <CopyButton value={account.user_id} label="Copy account id" />
        </span>
        <span>Locale {account.locale}</span>
      </CardFooter>
    </Card>
  );
}

/* -- devices -------------------------------------------------------------- */

/** Which glyph a platform gets. The platform string comes from the client
 *  that registered the installation, so anything unrecognised falls back to
 *  the neutral icon rather than guessing. */
function deviceIcon(platform: string) {
  const value = platform.toLowerCase();
  if (value.includes("ios") || value.includes("android") || value.includes("phone")) {
    return <Smartphone className="size-4" aria-hidden />;
  }
  if (value.includes("mac") || value.includes("windows") || value.includes("linux")) {
    return <Laptop className="size-4" aria-hidden />;
  }
  return <MonitorSmartphone className="size-4" aria-hidden />;
}

/** Whether this account is still signed in on an installation.
 *
 *  It is the BINDINGS that answer this, never `device.status`: a device is an
 *  installation shared across users over time, so its status says whether the
 *  machine may be used at all, not whether you are the one using it. Signing
 *  out deliberately leaves the device row active for the next person. */
function signedInHere(device: Device): boolean {
  return device.bindings.some((binding) => binding.status !== "revoked");
}

function DevicesCard({ onDone }: { onDone: (notice: DoneNotice) => void }) {
  const devices = useDevices();
  // Named for the API route, not for what the button says. The endpoint ends
  // this account's bindings and sessions on the installation and leaves the
  // device and its key alive, which is a sign-out.
  const signOutDevice = useRevokeDevice();
  // The two halves of "this machine is gone". Retiring ends the installation;
  // restoring is the undo, and the SERVER decides whether to offer it — see
  // `device.restorable`.
  const retireDevice = useRetireDevice();
  const restoreDevice = useRestoreDevice();
  const revokeBinding = useRevokeBinding();

  const rows: Device[] = devices.data?.devices ?? [];
  const failure =
    signOutDevice.error ?? retireDevice.error ?? restoreDevice.error ?? revokeBinding.error;

  return (
    <Card className="overflow-hidden">
      <CardHeader
        title="Devices"
        description="Every installation this account has signed in on, and what each one may sign in to. Signing out of one ends its sessions here; the installation stays registered so you can sign back in on it."
        action={
          devices.data ? (
            <span className="text-xs text-ink-4">
              {rows.length} {rows.length === 1 ? "device" : "devices"}
            </span>
          ) : null
        }
      />

      {failure ? (
        <CardBody className="pb-0">
          <Notice tone="danger" title="That sign-out failed">
            {errorMessage(failure)}
          </Notice>
        </CardBody>
      ) : null}

      {devices.isPending ? (
        <CardBody className="space-y-3">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
        </CardBody>
      ) : devices.error ? (
        <CardBody>
          <Notice tone="danger" title="Could not load your devices">
            {errorMessage(devices.error)}
          </Notice>
        </CardBody>
      ) : rows.length === 0 ? (
        <EmptyState
          size="sm"
          title="No devices yet"
          body="A device appears here the first time an application is installed and signs in on it."
        />
      ) : (
        <ul className="divide-y divide-line-soft">
          {rows.map((device) => (
            <li key={device.device_id}>
              <DeviceRow
                device={device}
                signOutDevice={signOutDevice}
                retireDevice={retireDevice}
                restoreDevice={restoreDevice}
                revokeBinding={revokeBinding}
                onDone={onDone}
              />
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function DeviceRow({
  device,
  signOutDevice,
  retireDevice,
  restoreDevice,
  revokeBinding,
  onDone,
}: {
  device: Device;
  signOutDevice: ReturnType<typeof useRevokeDevice>;
  retireDevice: ReturnType<typeof useRetireDevice>;
  restoreDevice: ReturnType<typeof useRestoreDevice>;
  revokeBinding: ReturnType<typeof useRevokeBinding>;
  onDone: (notice: DoneNotice) => void;
}) {
  // Two different facts, and conflating them is the bug this screen used to
  // have. `retired` is about the INSTALLATION — a retired one is dead for
  // everybody and needs a reinstall. `signedIn` is about this account on it.
  const retired = device.status !== "active";
  const signedIn = signedInHere(device);
  const name = device.device_name || device.installation_id;
  const busy = signOutDevice.isPending && signOutDevice.variables === device.device_id;
  const retiring = retireDevice.isPending && retireDevice.variables === device.device_id;
  const restoring = restoreDevice.isPending && restoreDevice.variables === device.device_id;

  // The versions are telemetry the installation reports when it starts, so an
  // installation that has not started since the feature shipped simply has
  // none. Each is rendered only when it is there: a row of "unknown" would
  // read as something wrong with the machine rather than as a gap in what it
  // has told us.
  const meta = [
    device.os_version ? `${device.platform} ${device.os_version}` : device.platform,
    device.app_version ? `app ${device.app_version}` : null,
    `last seen ${relativeTime(device.last_seen_at)}`,
  ].filter((part): part is string => part !== null);

  return (
    <div className="px-5 py-5 sm:px-6">
      <div className="flex flex-wrap items-start gap-4">
        <span
          aria-hidden
          className="grid size-9 shrink-0 place-items-center rounded-full bg-brand-softer text-brand ring-1 ring-brand/10"
        >
          {deviceIcon(device.platform)}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="truncate font-semibold text-ink">{name}</span>
            <StatusPill tone={signedIn ? "success" : "neutral"} dot>
              {signedIn ? "signed in" : "signed out"}
            </StatusPill>
            {retired ? (
              <StatusPill tone="danger">{humanizeState(device.status)}</StatusPill>
            ) : null}
          </div>
          <p className="mt-1 text-2xs text-ink-4">
            {meta.join(" · ")} ·{" "}
            <code className="font-mono">{truncateId(device.installation_id)}</code>
          </p>
        </div>
        {signedIn && !retired ? (
          <Button
            variant="secondary"
            size="sm"
            disabled={busy}
            onClick={async () => {
              const result = await signOutDevice.mutateAsync(device.device_id);
              onDone({
                title: "Signed out on that device",
                body: endedSummary("Signed out of", name, result),
              });
            }}
          >
            <BusyLabel busy={busy} idle="Sign out of this device" busyLabel="Signing out…" />
          </Button>
        ) : null}
        {/* Retire sits beside sign-out and is deliberately the quieter, more
            dangerous-looking of the two. It is offered on the same condition
            the server enforces — you must be signed in here — because only the
            machine's current occupant may end the installation. */}
        {signedIn && !retired ? (
          <Button
            variant="ghost"
            size="sm"
            disabled={retiring}
            onClick={async () => {
              const result = await retireDevice.mutateAsync(device.device_id);
              onDone({
                title: "Installation retired",
                body: `${endedSummary("Retired", name, result)} Nobody can sign in on that machine until you restore it.`,
              });
            }}
          >
            <BusyLabel busy={retiring} idle="I no longer have this machine" busyLabel="Retiring…" />
          </Button>
        ) : null}
        {/* The button only exists when the SERVER says it may. `restorable` is
            false for an installation Empite blocked, and the client must not
            try to work that out from `status` — it cannot, and guessing would
            offer an undo for the one decision that has none. */}
        {retired && device.restorable ? (
          <Button
            variant="secondary"
            size="sm"
            disabled={restoring}
            onClick={async () => {
              await restoreDevice.mutateAsync(device.device_id);
              onDone({
                title: "Installation restored",
                body: `${name} can be signed in on again. Open the app on that machine and sign in — restoring does not sign you in by itself.`,
              });
            }}
          >
            <BusyLabel busy={restoring} idle="Restore this installation" busyLabel="Restoring…" />
          </Button>
        ) : null}
      </div>

      {/* What signing out does NOT do is the half a customer cannot see, and
          the half they will otherwise assume: the row staying here with an
          active installation is not a failed revocation. */}
      {!signedIn && !retired ? (
        <p className="mt-3 text-2xs text-ink-4 sm:ml-13">
          You are signed out on this installation. It stays registered, so signing in on it again
          works normally — nothing was deleted from the machine.
        </p>
      ) : null}
      {/* Two retirements that look identical in `status` and are not: one the
          person did and can undo, one Empite did and nobody can. Telling them
          apart is exactly what `restorable` is for, and the old copy — "needs a
          fresh install" — was wrong for both. A reinstall does not clear the
          key in the OS keystore, so following it produces the same refusal
          again. */}
      {retired && device.restorable ? (
        <p className="mt-3 text-2xs text-ink-4 sm:ml-13">
          You retired this installation. Restoring it lets you sign in on that machine again with
          the app already installed there — nothing has to be reinstalled.
        </p>
      ) : null}
      {retired && !device.restorable ? (
        <p className="mt-3 text-2xs text-ink-4 sm:ml-13">
          This installation was retired by Empite and cannot be restored from here. Contact support
          if you still have the machine.
        </p>
      ) : null}

      {/* Signed-out bindings stay on screen: a list that drops what it just
          acted on gives the user no way to confirm it happened — and after a
          sign-out the binding row is the only evidence left, since the device
          deliberately stays active for the next person. */}
      {device.bindings.length === 0 ? (
        <p className="mt-3 ml-13 text-2xs text-ink-4">No application is bound to this device.</p>
      ) : (
        <ul className="mt-3 space-y-1.5 sm:ml-13">
          {device.bindings.map((binding) => (
            <li
              key={binding.binding_id}
              className="flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-field bg-surface-sunken/50 px-3 py-2"
            >
              <span className="min-w-0 flex-1 truncate text-sm font-semibold text-ink">
                {binding.application_name}
              </span>
              <StatusPill tone={binding.status === "active" ? "success" : "danger"}>
                {humanizeState(binding.status)}
              </StatusPill>
              <span className="text-2xs text-ink-4">
                {binding.last_used_at ? `used ${relativeTime(binding.last_used_at)}` : "never used"}
              </span>
              {binding.status === "active" ? (
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={revokeBinding.isPending && revokeBinding.variables === binding.binding_id}
                  onClick={async () => {
                    const result = await revokeBinding.mutateAsync(binding.binding_id);
                    onDone({
                      title: "Signed out of that application",
                      body: endedSummary(
                        "Signed out of",
                        `${binding.application_name} on ${name}`,
                        result,
                      ),
                    });
                  }}
                >
                  <BusyLabel
                    busy={
                      revokeBinding.isPending && revokeBinding.variables === binding.binding_id
                    }
                    idle="Sign out"
                    busyLabel="Signing out…"
                  />
                </Button>
              ) : (
                <BindingRevokedNote binding={binding} />
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function BindingRevokedNote({ binding }: { binding: Binding }) {
  if (!binding.revoked_at) return null;
  return (
    <span className="text-2xs text-ink-4">signed out {relativeTime(binding.revoked_at)}</span>
  );
}

/* -- sessions ------------------------------------------------------------- */

/** An `AppSession` as the ported table wants its rows keyed. */
type SessionRow = AppSession & { id: string; app?: ReturnType<typeof appIdentity> };

function sessionRows(
  sessions: AppSession[],
  applications: PortalApplication[],
): SessionRow[] {
  const identities = new Map(
    applications.map((application, index) => [application.id, appIdentity(application, index)]),
  );
  return sessions.map((session) => ({
    ...session,
    id: session.session_id,
    app: identities.get(session.application_id),
  }));
}

function SessionsCard({
  onDone,
  applications,
}: {
  onDone: (notice: DoneNotice) => void;
  applications: PortalApplication[];
}) {
  const sessions = useAppSessions();
  const revokeSession = useRevokeSession();

  const rows = useMemo(
    () => sessionRows(sessions.data?.sessions ?? [], applications),
    [sessions.data, applications],
  );
  const live = rows.filter((row) => row.live).length;

  const columns: Column<SessionRow>[] = [
    {
      key: "application",
      header: "Application",
      className: "w-56",
      cell: (row) => (
        // An application the customer no longer has still owns a session row,
        // so the badge falls back to the name the platform put on the session.
        row.app ? (
          <AppBadge app={row.app} showSlug />
        ) : (
          <span className="font-semibold text-ink">{row.application_name}</span>
        )
      ),
    },
    {
      key: "device",
      header: "Device",
      cell: (row) => (
        <span className="text-ink-2">
          {row.device_name || row.installation_id || "Browser"}
        </span>
      ),
    },
    {
      key: "from",
      header: "From",
      className: "w-44",
      cell: (row) => (
        <code className="font-mono text-2xs break-all text-ink-3">{row.ip || "—"}</code>
      ),
    },
    {
      key: "started",
      header: "Started",
      className: "w-44",
      cell: (row) => (
        <span className="tnum whitespace-nowrap text-ink-3">{formatTimestamp(row.created_at)}</span>
      ),
    },
    {
      key: "state",
      header: "State",
      className: "w-24",
      cell: (row) => (
        <StatusPill tone={row.live ? "success" : "neutral"} dot pulse={row.live}>
          {row.live ? "live" : "ended"}
        </StatusPill>
      ),
    },
    {
      key: "revoke",
      header: "",
      align: "right",
      className: "w-36",
      cell: (row) =>
        row.live ? (
          <Button
            variant="danger"
            size="sm"
            disabled={revokeSession.isPending && revokeSession.variables === row.session_id}
            onClick={async () => {
              const result = await revokeSession.mutateAsync(row.session_id);
              onDone({
                title: "Session revoked",
                body: endedSummary("Revoked", `the ${row.application_name} session`, result),
              });
            }}
          >
            <BusyLabel
              busy={revokeSession.isPending && revokeSession.variables === row.session_id}
              idle="End session"
              busyLabel="Ending…"
            />
          </Button>
        ) : null,
    },
  ];

  return (
    <Card className="overflow-hidden">
      <CardHeader
        title="Sessions"
        description="Where this account is signed in right now."
        action={
          sessions.data ? (
            <span className="text-xs text-ink-4">
              {live} live of {rows.length}
            </span>
          ) : null
        }
      />

      {revokeSession.error ? (
        <CardBody className="pb-0">
          <Notice tone="danger" title="That session could not be ended">
            {errorMessage(revokeSession.error)}
          </Notice>
        </CardBody>
      ) : null}

      {sessions.error ? (
        <CardBody>
          <Notice tone="danger" title="Could not load your sessions">
            {errorMessage(sessions.error)}
          </Notice>
        </CardBody>
      ) : (
        <DataTable
          columns={columns}
          rows={rows}
          caption="Application sessions on this account"
          empty={
            <EmptyState
              size="sm"
              title={sessions.isPending ? "Loading your sessions" : "No sessions yet"}
              body={
                sessions.isPending
                  ? undefined
                  : "An application that signs in on this account appears here."
              }
            />
          }
        />
      )}
    </Card>
  );
}
